import React from 'react';
import ReactMarkdown from 'react-markdown';
import type { AssistantMessageProps } from 'shared/panda/messages/panda-chat-message';
import { toMessageCards } from 'shared/panda/messages/doc-answer';
import { toHubOffer } from 'shared/panda/messages/hub-offer';
import { toRelatedResources } from 'shared/panda/messages/related-resources';
import { useAnswerCopyText } from 'shared/panda/messages/answer-copy.hooks';
import { pandaAnalytics } from '../../panda-analytics';
import styles from './render-messages.module.scss';
import { AnswerCard } from '../answer-card/answer-card';
import { MessageActions } from '../message-actions/message-actions';
import { HubOfferButton } from '../hub-offer';
import { RelatedResources } from '../related-resources/related-resources';
import { usePandaConversation } from '../../panda-chat-provider';

export function AssistantMessage({ message, isLast, precedesOffer }: AssistantMessageProps) {
  const { conversationId, loading, lang, hubSourceOf, isOfferFailed } = usePandaConversation();

  // The model often emits stray whitespace before the tool call; as markdown
  // that's an empty paragraph taking a gap slot. Trim before testing (as mobile).
  const intro = message.content?.trim() ?? '';
  const cards = toMessageCards(message);
  const relatedResources = toRelatedResources(message);
  const hasRelated = relatedResources.length > 0;
  const hubOffer = toHubOffer(message);
  const hasAnswer = Boolean(intro) || cards.length > 0 || hasRelated;
  // A hub answer (e.g. ASA): prefix it with the hub header and badge the card.
  const hubSource = hubSourceOf(message.id);
  const isHub = Boolean(hubSource);
  // The offer's accept was rejected outright: show the failure line where
  // the button was, instead of a generic error banner.
  const offerFailed = Boolean(hubOffer && isOfferFailed(hubOffer.offerId));
  // A hub run that settled with no answer card is a hub-generation miss: show the
  // failure line in place of the backend's (institution-attributed) template.
  const isHubFailure = isHub && cards.length === 0 && !(isLast && loading);
  // A content-only refusal before a hub offer belongs to that offer — suppress its
  // rating row. A plain refusal (no offer) or a related-documents list keeps it.
  const isRefusalBeforeOffer = Boolean(precedesOffer) && cards.length === 0 && !hasRelated;

  // The actions row appears once the response settles, not mid-stream.
  const showActions = !(isLast && loading);
  // The offer shows only while it's the last thing in the thread: a click
  // appends a tool message (and then the answer), and a follow-up appends a new
  // turn — both push the offer off the end, so it disappears without extra state.
  const showOffer = Boolean(hubOffer) && isLast;

  const copyText = useAnswerCopyText(message, lang, { isHub });

  // Nothing to show → render nothing: a consumed offer (accepted or superseded)
  // must not leave an empty container in the gap-spaced column.
  if (!hasAnswer && !isHub && !showOffer && !offerFailed) return null;

  const actionEventProperties = {
    conversationId,
    messageId: message.id,
  };

  return (
    <div className={styles.answer}>
      {isHub && cards.length > 0 && (
        <div className={styles.hubHeader}>{lang.ASA_ANSWER_HEADER}</div>
      )}

      {isHubFailure && <div className={styles.intro}>{lang.ASA_FAILURE}</div>}

      {/* A hub answer's text is suppressed (a success has none, a failure's is
          replaced above); a related-documents list draws its own lead-in. */}
      {!!intro && !isHub && !hasRelated && (
        <div className={styles.intro}>
          <ReactMarkdown>{intro}</ReactMarkdown>
        </div>
      )}

      {hasRelated && <RelatedResources intro={intro} resources={relatedResources} />}

      {cards.map((card, index) => (
        <AnswerCard
          key={card.key}
          card={card}
          conversationId={conversationId}
          messageId={message.id}
          position={index + 1}
          hubSource={hubSource}
        />
      ))}

      {showOffer && hubOffer && (
        <>
          <div className={styles.intro}>
            {hubOffer.variant === 'additional'
              ? lang.ASA_OFFER_ADDITIONAL
              : lang.ASA_OFFER_FALLBACK}
          </div>
          <HubOfferButton offer={hubOffer} messageId={message.id} />
        </>
      )}

      {offerFailed && <div className={styles.intro}>{lang.ASA_FAILURE}</div>}

      {/* Rate any settled response — an answer or the hub failure line — except a
          refusal that precedes a hub offer (that refusal is part of the offer). */}
      {showActions && (hasAnswer || isHubFailure) && !isRefusalBeforeOffer && (
        <MessageActions
          copyText={isHubFailure ? lang.ASA_FAILURE : copyText}
          onCopy={() => pandaAnalytics.trackCopy(actionEventProperties)}
          onFeedback={value => pandaAnalytics.trackFeedback(value, actionEventProperties)}
        />
      )}
    </div>
  );
}
